import { action, computed, observable, makeAutoObservable } from 'mobx';
import { FormInputState, FormModel, ResultValue, group, Result } from 'src_common/common/mobx-utils/Form';
import { InputStatus } from 'src/components/Input/InputStatus';
import {
    validateExpectLink,
    validateExpectNumber,
    validateRequire,
    validateExpectValueInPx,
} from 'src/api/utils/formValidators';
import { TextareaStatus } from 'src/components/FormComponents/Textarea/TextareaStatus';
import { UploadImageState } from '../../UploadImageState/UploadImageState';
import { MarketWithCustomSelectionsState } from './MarketWithCustomSelectionsState';
import { ParamsType } from 'src/api_openapi/generated/openapi_website_cms_createEventDetails';
import { apiCommon } from 'src/api/ApiCommon';
import { EventDetailsModelType } from 'src/api/config/event_details/get_event_details_list';
import { decodeCustomSelections, CustomSelectionsType } from './commonCmsGuard';
import { MarketsFiltersState } from './MarketsFiltersState';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { ModelsState } from 'src_common/common/websocket2/ModelsState';
import { CustomSelectionsTemplateState } from 'src/AppState/CmsState/EventDetailsState/CustomSelectionsTemplate/CustomSelectionsTemplateState';
import { TypeId } from 'src_common/common/websocket2/type';

type EventDetailsBodyType = ParamsType['body'];

export type EventDetailsModelParsedType = Omit<EventDetailsModelType, 'custom_selections'> & {
    custom_selections: Array<CustomSelectionsType>;
};

interface EventDetailsLoading {
    type: 'loading';
}

interface EventDetailsReady {
    type: 'ready';
    value: Array<EventDetailsModelType>;
}

interface EventDetailsError {
    type: 'error';
    value: string;
}

type EventDetailsContentResponse = EventDetailsLoading | EventDetailsReady | EventDetailsError;

interface EventDetailsValidFormType {
    name: string;
    streamUrl: string;
    overViewText: string;
    layoutType: string;
    description: string;
    headerBgColor: string;
    promoBgColor: string;
    headerImgHeightSmall: string;
    headerImgHeightMedium: string;
    headerImgHeightBig: string;
}

interface OpenSectionsType {
    basic: boolean;
    markets: boolean;
    headerImages: boolean;
    headerStreamImages: boolean;
    promoImages: boolean;
    marketsFilters: boolean;
}

export type KeyOfOpenSectionsType = keyof OpenSectionsType;
export class EventDetailsState {
    private readonly marketsWithCustomSelectionsMap: MobxMapAutoNew<TypeId.MarketId, MarketWithCustomSelectionsState>;
    public readonly customSelectionsTemplateState: CustomSelectionsTemplateState;

    @observable private readonly eventDetailsListResource: Resource<Array<EventDetailsModelType>>;
    @observable private eventModelId: number | null = null;
    @observable private isCheckEvent: boolean = false;
    @observable private inEditEventDetails: EventDetailsModelParsedType | null = null;
    @observable public marketsWithCustomSelectionsIdList: Array<TypeId.MarketId> = [];
    @observable public isEditMode: boolean = false;
    @observable public isShowMarketFiltersFutureFlag: boolean = false;

    @observable public isOpenSections: OpenSectionsType = {
        basic: true,
        markets: true,
        headerImages: true,
        headerStreamImages: true,
        promoImages: true,
        marketsFilters: true,
    };

    @observable public marketsFiltersState: MarketsFiltersState | null;

    public readonly eventIdModel: FormModel<number | null>;
    public readonly eventId: FormInputState<string>;
    public readonly eventIdStatus: InputStatus;
    public readonly nameModel: FormModel<string>;
    public readonly name: FormInputState<string>;
    public readonly nameStatus: InputStatus;
    public readonly streamUrlModel: FormModel<string>;
    public readonly streamUrl: FormInputState<string>;
    public readonly streamUrlStatus: InputStatus;
    public readonly overViewTextModel: FormModel<string>;
    public readonly overViewText: FormInputState<string>;
    public readonly overViewTextStatus: InputStatus;
    public readonly headerBgColor: FormInputState<string>;
    public readonly headerBgColorModel: FormModel<string>;
    public readonly headerBgColorStatus: InputStatus;
    public readonly promoBgColor: FormInputState<string>;
    public readonly promoBgColorModel: FormModel<string>;
    public readonly promoBgColorStatus: InputStatus;
    public readonly layoutTypeModel: FormModel<string>;
    public readonly layoutType: FormInputState<string>;
    public readonly layoutTypeStatus: InputStatus;
    public readonly descriptionModel: FormModel<string>;
    public readonly description: FormInputState<string>;
    public readonly descriptionStatus: TextareaStatus;
    public readonly selectedMarketIdModel: FormModel<TypeId.MarketId | null>;
    public readonly selectedMarketId: FormInputState<TypeId.MarketId | null>;
    public readonly selectedMarketIdStatus: InputStatus;

    public readonly headerImgHeightSmallModel: FormModel<string>;
    public readonly headerImgHeightSmall: FormInputState<string>;
    public readonly headerImgHeightSmallStatus: InputStatus;
    public readonly headerImgHeightMediumModel: FormModel<string>;
    public readonly headerImgHeightMedium: FormInputState<string>;
    public readonly headerImgHeightMediumStatus: InputStatus;
    public readonly headerImgHeightBigModel: FormModel<string>;
    public readonly headerImgHeightBig: FormInputState<string>;
    public readonly headerImgHeightBigStatus: InputStatus;

    private readonly form: FormModel<EventDetailsValidFormType>;

    public uploadHeaderImgSmall: UploadImageState;
    public uploadHeaderImgMedium: UploadImageState;
    public uploadHeaderImgBig: UploadImageState;

    public uploadHeaderUnderStreamImgSmall: UploadImageState;
    public uploadHeaderUnderStreamImgMedium: UploadImageState;
    public uploadHeaderUnderStreamImgBig: UploadImageState;

    public uploadPromoImgSmall: UploadImageState;
    public uploadPromoImgMedium: UploadImageState;
    public uploadPromoImgBig: UploadImageState;

    constructor(private readonly modelsState: ModelsState) {
        this.eventDetailsListResource = new Resource(async (): Promise<Array<EventDetailsModelType>> => {
            return await apiCommon.get_event_details_list.run({});
        });

        this.customSelectionsTemplateState = new CustomSelectionsTemplateState();

        this.marketsWithCustomSelectionsMap = new MobxMapAutoNew(
            (marketId: TypeId.MarketId): MarketWithCustomSelectionsState =>
                new MarketWithCustomSelectionsState(
                    this.modelsState,
                    this.customSelectionsTemplateState,
                    marketId,
                    this.handleDeleteMarket
                )
        );

        this.marketsFiltersState = null;

        this.uploadHeaderImgSmall = new UploadImageState('event-details/header/small');
        this.uploadHeaderImgMedium = new UploadImageState('event-details/header/medium');
        this.uploadHeaderImgBig = new UploadImageState('event-details/header/big');

        this.uploadHeaderUnderStreamImgSmall = new UploadImageState('event-details/header_stream/small');
        this.uploadHeaderUnderStreamImgMedium = new UploadImageState('event-details/header_stream/medium');
        this.uploadHeaderUnderStreamImgBig = new UploadImageState('event-details/header_stream/big');

        this.uploadPromoImgSmall = new UploadImageState('event-details/promo/small');
        this.uploadPromoImgMedium = new UploadImageState('event-details/promo/medium');
        this.uploadPromoImgBig = new UploadImageState('event-details/promo/big');

        this.eventId = new FormInputState('', false);
        this.eventIdModel = this.eventId.map(validateExpectNumber);
        this.eventIdStatus = new InputStatus(this.eventIdModel);

        this.name = new FormInputState('', false);
        this.nameModel = this.name.toModel();
        this.nameStatus = new InputStatus(this.nameModel);

        this.streamUrl = new FormInputState('', false);
        this.streamUrlModel = this.streamUrl.map(validateExpectLink);
        this.streamUrlStatus = new InputStatus(this.streamUrlModel);

        this.overViewText = new FormInputState('', false);
        this.overViewTextModel = this.overViewText.toModel();
        this.overViewTextStatus = new InputStatus(this.overViewTextModel);

        this.headerBgColor = new FormInputState('', false);
        this.headerBgColorModel = this.headerBgColor.toModel();
        this.headerBgColorStatus = new InputStatus(this.headerBgColorModel);

        this.promoBgColor = new FormInputState('', false);
        this.promoBgColorModel = this.promoBgColor.toModel();
        this.promoBgColorStatus = new InputStatus(this.promoBgColorModel);

        this.layoutType = new FormInputState('hero', false);
        this.layoutTypeModel = this.layoutType.map(validateRequire);
        this.layoutTypeStatus = new InputStatus(this.layoutTypeModel);

        this.description = new FormInputState('', false);
        this.descriptionModel = this.description.toModel();
        this.descriptionStatus = new TextareaStatus(this.descriptionModel);

        this.selectedMarketId = new FormInputState(null, false);
        this.selectedMarketIdModel = this.selectedMarketId.map(
            (value: TypeId.MarketId | null): Result<TypeId.MarketId> => {
                return new ResultValue(value);
            }
        );
        this.selectedMarketIdStatus = new InputStatus(this.selectedMarketIdModel);

        this.headerImgHeightSmall = new FormInputState('', false);
        this.headerImgHeightSmallModel = this.headerImgHeightSmall.map(validateExpectValueInPx);
        this.headerImgHeightSmallStatus = new InputStatus(this.headerImgHeightSmallModel);

        this.headerImgHeightMedium = new FormInputState('', false);
        this.headerImgHeightMediumModel = this.headerImgHeightMedium.map(validateExpectValueInPx);
        this.headerImgHeightMediumStatus = new InputStatus(this.headerImgHeightMediumModel);

        this.headerImgHeightBig = new FormInputState('', false);
        this.headerImgHeightBigModel = this.headerImgHeightBig.map(validateExpectValueInPx);
        this.headerImgHeightBigStatus = new InputStatus(this.headerImgHeightBigModel);

        this.form = group({
            name: this.nameModel,
            streamUrl: this.streamUrlModel,
            overViewText: this.overViewTextModel,
            layoutType: this.layoutTypeModel,
            description: this.descriptionModel,
            headerBgColor: this.headerBgColorModel,
            promoBgColor: this.promoBgColorModel,
            headerImgHeightSmall: this.headerImgHeightSmallModel,
            headerImgHeightMedium: this.headerImgHeightMediumModel,
            headerImgHeightBig: this.headerImgHeightBigModel,
        }).map((item: EventDetailsValidFormType): Result<EventDetailsValidFormType> => {
            return new ResultValue({
                name: item.name,
                streamUrl: item.streamUrl,
                overViewText: item.overViewText,
                layoutType: item.layoutType,
                description: item.description,
                headerBgColor: item.headerBgColor,
                promoBgColor: item.promoBgColor,
                headerImgHeightSmall: item.headerImgHeightSmall,
                headerImgHeightMedium: item.headerImgHeightMedium,
                headerImgHeightBig: item.headerImgHeightBig,
            });
        });
        makeAutoObservable(this);
    }

    @computed public get eventIdValue(): number | null {
        const eventId = this.eventIdModel.result;

        if (eventId instanceof ResultValue && eventId.value !== null) {
            return eventId.value;
        }
        return null;
    }

    private getMarketById = (id: TypeId.MarketId): MarketModel | null => {
        return this.markets.find((elem) => elem.id === id) ?? null;
    };

    public getMarketWithCustomSelectionsModelById = (id: TypeId.MarketId): MarketWithCustomSelectionsState => {
        return this.marketsWithCustomSelectionsMap.get(id);
    };

    @computed public get selectedMarketName(): string | null {
        const selectedMarketId = this.selectedMarketIdModel.result;
        if (selectedMarketId instanceof ResultValue && selectedMarketId.value !== null) {
            const market = this.getMarketById(selectedMarketId.value);
            if (market !== null) {
                return market.name;
            }
        }

        return null;
    }

    @computed public get checkEventStatus(): 'ok' | 'error' | 'default' {
        if (this.isCheckEvent && this.eventModel !== null) {
            return 'ok';
        }
        if (this.isCheckEvent && this.eventModel === null) {
            return 'error';
        }
        return 'default';
    }

    @computed public get checkEventLabel(): string {
        if (this.checkEventStatus === 'ok') {
            return 'Event valid';
        }
        if (this.checkEventStatus === 'error') {
            if (this.eventIdValue !== null && this.eventDetailsIds.includes(this.eventIdValue)) {
                return 'Event already exist';
            }
            return 'Event invalid';
        }

        return 'Check event';
    }

    @computed public get markets(): Array<MarketModel> {
        if (this.eventModel !== null) {
            return this.eventModel.markets;
        }

        return [];
    }

    @computed public get eventDetailsList(): Array<EventDetailsModelParsedType> {
        if (this.eventDetailsListResponse.type === 'ready') {
            const eventDetailsList = this.eventDetailsListResponse.value.map((elem) => {
                const custom_selections = decodeCustomSelections(elem.custom_selections);
                return {
                    ...elem,
                    custom_selections: custom_selections,
                };
            });
            return eventDetailsList.sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : 1));
        }
        return [];
    }

    @computed private get eventDetailsIds(): Array<number> {
        return this.eventDetailsList.map((elem) => elem.event_id);
    }

    @computed private get marketsAndSelectionsDataForSubmit(): Array<CustomSelectionsType> {
        const marketDataList: Array<CustomSelectionsType> = [];

        for (const marketId of this.marketsWithCustomSelectionsIdList) {
            const marketModel = this.getMarketWithCustomSelectionsModelById(marketId);
            const market: CustomSelectionsType = {
                market_id: TypeId.getMarketId(marketId),
                selection_type: marketModel.selectionTemplateDataNew,
                selections: marketModel.selectionsData,
            };

            marketDataList.push(market);
        }

        return marketDataList;
    }

    @computed private get eventDetailsListResponse(): EventDetailsContentResponse {
        const eventDetails = this.eventDetailsListResource.get();
        if (eventDetails.type === 'ready' && eventDetails.value !== null) {
            return {
                type: 'ready',
                value: eventDetails.value,
            };
        }
        if (eventDetails.type === 'loading') {
            return {
                type: 'loading',
            };
        }

        return {
            type: 'error',
            value: 'Something went wrong with fetching event details info',
        };
    }

    @action public handleToggleOpenSections = (selection: KeyOfOpenSectionsType): void => {
        this.isOpenSections = {
            ...this.isOpenSections,
            [selection]: !this.isOpenSections[selection],
        };
    };

    @computed get eventModel(): EventModel | null {
        if (this.eventModelId !== null) {
            return this.modelsState.getEvent(this.eventModelId);
        }

        return null;
    }

    @action public handleCheckEvent = async (): Promise<void> => {
        const eventId = this.eventIdModel.result;
        this.eventModelId = null;

        if (eventId instanceof ResultValue && eventId.value !== null) {
            if (!this.eventDetailsIds.includes(eventId.value)) {
                this.eventModelId = eventId.value;
                this.marketsFiltersState = this.createMarketFiltersState(eventId.value);
            }
        }
        this.isCheckEvent = true;
    };

    @action private createMarketFiltersState = (eventId: number, editData?: unknown): MarketsFiltersState => {
        const data = editData ?? null;
        return new MarketsFiltersState(this.modelsState, eventId, data);
    };

    @action public handleChangeEventId = async (): Promise<void> => {
        this.isCheckEvent = false;
        this.resetForm();
        this.handleDeleteAllMarkets();
    };

    @action public handlePopulatePredefineData = async (): Promise<void> => {
        if (this.checkEventStatus === 'ok' && this.eventModel !== null) {
            this.name.setValue(this.eventModel.name);
        }
    };

    @action public handleOnAddCustomSelections = async (): Promise<void> => {
        const selectedMarketId = this.selectedMarketIdModel.result;
        if (selectedMarketId instanceof ResultValue && selectedMarketId.value !== null) {
            this.marketsWithCustomSelectionsIdList.push(selectedMarketId.value);
            this.selectedMarketId.setValue(null);
        }
    };

    @action private handleDeleteMarket = (id: TypeId.MarketId): void => {
        if (this.eventModel !== null) {
            this.marketsWithCustomSelectionsIdList = this.marketsWithCustomSelectionsIdList.filter(
                (elem) => elem !== id
            );
            this.marketsWithCustomSelectionsMap.delete(id);
        }
    };

    @action private handleDeleteAllMarkets = (): void => {
        if (this.eventModel !== null) {
            for (const marketId of this.marketsWithCustomSelectionsIdList) {
                this.marketsWithCustomSelectionsMap.delete(marketId);
            }
            this.marketsWithCustomSelectionsIdList = [];
        }
    };

    @action private getEventDetailsPayload = (): EventDetailsBodyType | null => {
        this.form.setAsVisited();
        const result = this.form.result;
        const eventIdModel = this.eventIdModel.result;
        const marketFilters = this.marketsFiltersState?.prepareData ?? [];

        if (eventIdModel instanceof ResultValue) {
            const eventId = eventIdModel.value;
            if (result instanceof ResultValue && eventId !== null) {
                return {
                    event_id: eventId,
                    name: result.value.name,
                    stream_url: result.value.streamUrl,
                    over_view_text: result.value.overViewText,
                    description: result.value.description,
                    header_img_small: this.uploadHeaderImgSmall.imgUrl,
                    header_img_medium: this.uploadHeaderImgMedium.imgUrl,
                    header_img_big: this.uploadHeaderImgBig.imgUrl,
                    header_img_under_stream_small: this.uploadHeaderUnderStreamImgSmall.imgUrl,
                    header_img_under_stream_medium: this.uploadHeaderUnderStreamImgMedium.imgUrl,
                    header_img_under_stream_big: this.uploadHeaderUnderStreamImgBig.imgUrl,
                    promo_img_small: this.uploadPromoImgSmall.imgUrl,
                    promo_img_medium: this.uploadPromoImgMedium.imgUrl,
                    promo_img_big: this.uploadPromoImgBig.imgUrl,
                    header_bg_color: result.value.headerBgColor,
                    promo_bg_color: result.value.promoBgColor,
                    layout_type: result.value.layoutType,
                    is_active: false,
                    is_active_promo: false,
                    is_active_stream: false,
                    is_active_details: false,
                    custom_selections: this.marketsAndSelectionsDataForSubmit,
                    market_filters: marketFilters,
                    header_img_height_small: result.value.headerImgHeightSmall,
                    header_img_height_medium: result.value.headerImgHeightMedium,
                    header_img_height_big: result.value.headerImgHeightBig,
                };
            }
        }

        return null;
    };

    @action public handleCreate = async (): Promise<void> => {
        const payload = this.getEventDetailsPayload();

        if (payload !== null) {
            const submitResult = await this.handleCreateApiCall(payload);

            if (submitResult !== null) {
                await this.eventDetailsListResource.refresh();
                this.eventIdModel.reset();
                this.resetForm();
            } else {
                console.log('Error');
            }
        }
    };

    @action public handleEdit = async (): Promise<void> => {
        const payloadRaw = this.getEventDetailsPayload();

        if (payloadRaw !== null && this.inEditEventDetails !== null) {
            const payload = {
                ...payloadRaw,
                is_active: this.inEditEventDetails.is_active,
                is_active_promo: this.inEditEventDetails.is_active_promo,
                is_active_stream: this.inEditEventDetails.is_active_stream,
            };

            const submitResult = await this.handleEditApiCall(payload, this.inEditEventDetails.id);

            if (submitResult !== null) {
                await this.eventDetailsListResource.refresh();
                this.eventIdModel.reset();
                this.resetForm();
            } else {
                console.log('Error');
            }
        }
    };

    @action public handleReset = (): void => {
        if (this.isEditMode && this.inEditEventDetails !== null) {
            this.setEditEventDetails(this.inEditEventDetails);
        } else {
            this.resetForm();
        }
    };

    @action public resetForm = (): void => {
        this.form.reset();
        this.handleDeleteAllMarkets();
        this.resetImages();
        this.isCheckEvent = false;
        this.isEditMode = false;
        this.inEditEventDetails = null;
        this.marketsFiltersState = null;
    };

    @action private resetImages = (): void => {
        this.uploadHeaderImgSmall.handleResetImage();
        this.uploadHeaderImgMedium.handleResetImage();
        this.uploadHeaderImgBig.handleResetImage();
        this.uploadHeaderUnderStreamImgSmall.handleResetImage();
        this.uploadHeaderUnderStreamImgMedium.handleResetImage();
        this.uploadHeaderUnderStreamImgBig.handleResetImage();
        this.uploadPromoImgSmall.handleResetImage();
        this.uploadPromoImgMedium.handleResetImage();
        this.uploadPromoImgBig.handleResetImage();
    };

    @action public setEditMode = (value: boolean): void => {
        this.isEditMode = value;
    };

    @action public setEventId = (id: number): void => {
        this.eventModelId = id;
    };

    @action public setEditEventDetails = async (eventDetails: EventDetailsModelParsedType): Promise<void> => {
        this.eventModelId = eventDetails.event_id;
        this.isCheckEvent = true;
        this.inEditEventDetails = eventDetails;
        const eventId = eventDetails.event_id ?? '';
        this.eventId.setValue(eventId.toString());
        this.name.setValue(eventDetails.name ?? '');
        this.streamUrl.setValue(eventDetails.stream_url ?? '');
        this.overViewText.setValue(eventDetails.over_view_text ?? '');
        this.layoutType.setValue(eventDetails.layout_type);
        this.description.setValue(eventDetails.description ?? '');
        this.headerImgHeightSmall.setValue(eventDetails.header_img_height_small ?? '');
        this.headerImgHeightMedium.setValue(eventDetails.header_img_height_medium ?? '');
        this.headerImgHeightBig.setValue(eventDetails.header_img_height_big ?? '');
        this.setEditCustomSelections(eventDetails.custom_selections);
        this.setEditImages(eventDetails);
        this.marketsFiltersState = this.createMarketFiltersState(eventDetails.event_id, eventDetails.market_filters);
    };

    @action private setEditCustomSelections = (customSelections: Array<CustomSelectionsType>): void => {
        if (this.eventIdValue === null) {
            console.error('setEditCustomSelections eventIdValue - null - ignore action');
            return;
        }

        for (const customSelection of customSelections) {
            const marketId = TypeId.newMarketId(this.eventIdValue, customSelection.market_id);
            const marketModel = this.getMarketWithCustomSelectionsModelById(marketId);
            marketModel.setCustomSelections(customSelection);
            this.marketsWithCustomSelectionsIdList.push(marketId);
        }
    };

    @action private setEditImages = (eventDetails: EventDetailsModelType): void => {
        this.uploadHeaderImgSmall.handleChooseImage(eventDetails.header_img_small);
        this.uploadHeaderImgMedium.handleChooseImage(eventDetails.header_img_medium);
        this.uploadHeaderImgBig.handleChooseImage(eventDetails.header_img_big);
        this.uploadHeaderUnderStreamImgSmall.handleChooseImage(eventDetails.header_img_under_stream_small);
        this.uploadHeaderUnderStreamImgMedium.handleChooseImage(eventDetails.header_img_under_stream_medium);
        this.uploadHeaderUnderStreamImgBig.handleChooseImage(eventDetails.header_img_under_stream_big);
        this.uploadPromoImgSmall.handleChooseImage(eventDetails.promo_img_small);
        this.uploadPromoImgMedium.handleChooseImage(eventDetails.promo_img_medium);
        this.uploadPromoImgBig.handleChooseImage(eventDetails.promo_img_big);
    };

    @action public setActiveFlag = (eventDetails: EventDetailsModelParsedType): void => {
        const payload: EventDetailsModelParsedType = {
            ...eventDetails,
            is_active: !eventDetails.is_active,
        };

        this.handleEditActiveFlags(payload);
    };

    @action public setActivePromoFlag = (eventDetails: EventDetailsModelParsedType): void => {
        const payload: EventDetailsModelParsedType = {
            ...eventDetails,
            is_active_promo: !eventDetails.is_active_promo,
        };

        this.handleEditActiveFlags(payload);
    };

    @action public setActiveStreamFlag = (eventDetails: EventDetailsModelParsedType): void => {
        const payload: EventDetailsModelParsedType = {
            ...eventDetails,
            is_active_stream: !eventDetails.is_active_stream,
        };

        this.handleEditActiveFlags(payload);
    };

    @action public setActiveDetailsFlag = (eventDetails: EventDetailsModelParsedType): void => {
        const payload: EventDetailsModelParsedType = {
            ...eventDetails,
            is_active_details: !eventDetails.is_active_details,
        };

        this.handleEditActiveFlags(payload);
    };

    @action private handleEditActiveFlags = async (payload: EventDetailsModelParsedType) => {
        const submitResult = await this.handleEditApiCall(payload, payload.id);

        if (submitResult !== null) {
            await this.eventDetailsListResource.refresh();
            this.resetForm();
        } else {
            console.log('Error');
        }
    };

    @action private handleCreateApiCall = async (eventDetails: EventDetailsBodyType): Promise<string | null> => {
        let response: string;
        try {
            response = await apiCommon.post_create_event_details.run({ data: eventDetails });
        } catch (error) {
            console.error(error);
            return null;
        }
        return response;
    };

    @action private handleEditApiCall = async (
        eventDetails: EventDetailsBodyType,
        id: number
    ): Promise<string | null> => {
        let response: string;
        try {
            response = await apiCommon.put_update_event_details.run({ data: eventDetails, id });
        } catch (error) {
            console.error(error);
            return null;
        }
        return response;
    };

    @action public handleRemoveApiCall = async (id: number): Promise<void> => {
        const removeResult = await apiCommon.delete_event_details.run({ id });

        if (removeResult) {
            await this.eventDetailsListResource.refresh();
        } else {
            console.log('Error');
        }
    };
}
